import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';

export const CartDrawer = () => {
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    cartTotal,
    isCartOpen,
    setIsCartOpen,
    proceedToCheckout,
    checkoutLoading,
    appliedDiscountCode,
    discountPercent,
    discountAmount,
    discountedTotal,
  } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent
        side="right"
        className="bg-white border-amber-100 w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader className="pb-4 border-b border-amber-100">
          <SheetTitle className="font-serif text-xl text-gold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Your Cart ({cartItems.length})
          </SheetTitle>
          <SheetDescription className="text-muted-foreground">
            Review your items before checkout
          </SheetDescription>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-24 h-24 rounded-full bg-cream flex items-center justify-center mb-4">
              <ShoppingBag className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-lg text-foreground mb-2">
              Your cart is empty
            </h3>
            <p className="text-muted-foreground text-sm text-center mb-6">
              Explore our collection and add some spiritual items to your cart
            </p>
            <Button
              onClick={() => setIsCartOpen(false)}
              className="gold-gradient text-white hover:opacity-90"
              asChild
              data-testid="cart-browse-btn"
            >
              <Link to="/products">Browse Products</Link>
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 py-4">
              <AnimatePresence>
                {cartItems.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex gap-4 py-4 border-b border-amber-100"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-cream flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-amber-50">
                          <ShoppingBag className="w-7 h-7 text-amber-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground text-sm truncate">
                        {item.name}
                      </h4>
                      <p className="text-gold font-semibold mt-1">
                        {formatPrice(item.price)}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="w-7 h-7 border-amber-200"
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                          data-testid={`cart-decrease-${item.id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center text-sm font-medium">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="w-7 h-7 border-amber-200"
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                          data-testid={`cart-increase-${item.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive flex-shrink-0"
                      onClick={() => removeFromCart(item.id)}
                      data-testid={`cart-remove-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </ScrollArea>

            <div className="pt-4 border-t border-amber-100 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground font-medium">
                  {formatPrice(cartTotal)}
                </span>
              </div>
              {appliedDiscountCode && discountAmount > 0 && (
                <div className="flex justify-between items-center" data-testid="cart-discount-row">
                  <span className="text-green-600 font-medium text-sm">
                    {appliedDiscountCode} ({discountPercent}% off)
                  </span>
                  <span className="text-green-600 font-semibold">- {formatPrice(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-muted-foreground text-sm">Calculated at checkout</span>
              </div>
              <Separator className="bg-amber-100" />
              <div className="flex justify-between items-center">
                <span className="text-foreground font-semibold">Total</span>
                <span className="text-gold font-bold text-xl" data-testid="cart-total">
                  {formatPrice(appliedDiscountCode ? discountedTotal : cartTotal)}
                </span>
              </div>
              {appliedDiscountCode && (
                <p className="text-[11px] text-center text-green-600">
                  {discountPercent}% bundle discount ({appliedDiscountCode}) applied at checkout
                </p>
              )}
              <Button
                className="w-full gold-gradient text-white hover:opacity-90 h-12 text-base font-semibold"
                onClick={proceedToCheckout}
                disabled={checkoutLoading}
                data-testid="checkout-btn"
              >
                {checkoutLoading ? 'Redirecting to Shopify...' : 'Proceed to Checkout'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Secure checkout · UPI / Cards / COD
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
